import React, { useRef, useState, forwardRef, useImperativeHandle, useEffect, useMemo } from 'react';
import api from '../config/api';
import { useLanguage } from '../context/LanguageContext';
import {
  buildTravelFormStateFromOrder,
  syncRowsFromPrimary,
  validateTravelFormClient,
} from '../utils/travelInformationForm';
import './TicketOrderTravelForm.css';

function TravelLegFields({ legKey, leg, onChange, t, idPrefix }) {
  const mode = leg.transportMode;

  const update = (field, value) => {
    onChange({ ...leg, [field]: value });
  };

  return (
    <div className="travel-leg-fields">
      <div className="attendee-fields">
        <div className="attendee-field">
          <label htmlFor={`${idPrefix}-${legKey}-country`}>{t('ticketTravel.departureCountry')}</label>
          <input
            id={`${idPrefix}-${legKey}-country`}
            type="text"
            value={leg.departureCountry}
            onChange={(e) => update('departureCountry', e.target.value)}
            required
          />
        </div>
        <div className="attendee-field">
          <label htmlFor={`${idPrefix}-${legKey}-city`}>{t('ticketTravel.departureCity')}</label>
          <input
            id={`${idPrefix}-${legKey}-city`}
            type="text"
            value={leg.departureCity}
            onChange={(e) => update('departureCity', e.target.value)}
            required
          />
        </div>
        <div className="attendee-field">
          <label htmlFor={`${idPrefix}-${legKey}-date`}>{t('ticketTravel.date')}</label>
          <input
            id={`${idPrefix}-${legKey}-date`}
            type="date"
            value={leg.date}
            onChange={(e) => update('date', e.target.value)}
            required
          />
        </div>
        <div className="attendee-field">
          <label htmlFor={`${idPrefix}-${legKey}-time`}>{t('ticketTravel.arrivalTime')}</label>
          <input
            id={`${idPrefix}-${legKey}-time`}
            type="time"
            value={leg.arrivalTime}
            onChange={(e) => update('arrivalTime', e.target.value)}
            required
          />
        </div>
        <div className="attendee-field">
          <label htmlFor={`${idPrefix}-${legKey}-mode`}>{t('ticketTravel.transportMode')}</label>
          <select
            id={`${idPrefix}-${legKey}-mode`}
            value={leg.transportMode}
            onChange={(e) => update('transportMode', e.target.value)}
            required
          >
            <option value="">{t('ticketTravel.selectTransport')}</option>
            <option value="car">{t('ticketTravel.transport.car')}</option>
            <option value="train">{t('ticketTravel.transport.train')}</option>
            <option value="plane">{t('ticketTravel.transport.plane')}</option>
            <option value="bus">{t('ticketTravel.transport.bus')}</option>
          </select>
        </div>
      </div>

      {mode === 'car' && (
        <div className="attendee-fields travel-mode-fields">
          <div className="attendee-field">
            <label htmlFor={`${idPrefix}-${legKey}-plate`}>{t('ticketTravel.licensePlate')}</label>
            <input
              id={`${idPrefix}-${legKey}-plate`}
              type="text"
              value={leg.licensePlate}
              onChange={(e) => update('licensePlate', e.target.value)}
              required
            />
          </div>
        </div>
      )}

      {mode === 'train' && (
        <div className="attendee-fields travel-mode-fields">
          <div className="attendee-field">
            <label htmlFor={`${idPrefix}-${legKey}-train-num`}>{t('ticketTravel.trainNumber')}</label>
            <input
              id={`${idPrefix}-${legKey}-train-num`}
              type="text"
              value={leg.trainNumber}
              onChange={(e) => update('trainNumber', e.target.value)}
              required
            />
          </div>
          <div className="attendee-field">
            <label htmlFor={`${idPrefix}-${legKey}-train-station`}>{t('ticketTravel.trainStation')}</label>
            <input
              id={`${idPrefix}-${legKey}-train-station`}
              type="text"
              value={leg.trainStation}
              onChange={(e) => update('trainStation', e.target.value)}
              required
            />
          </div>
          <div className="attendee-field">
            <label htmlFor={`${idPrefix}-${legKey}-train-city`}>{t('ticketTravel.trainStationCity')}</label>
            <input
              id={`${idPrefix}-${legKey}-train-city`}
              type="text"
              value={leg.trainStationCity}
              onChange={(e) => update('trainStationCity', e.target.value)}
              required
            />
          </div>
        </div>
      )}

      {mode === 'plane' && (
        <div className="attendee-fields travel-mode-fields">
          <div className="attendee-field">
            <label htmlFor={`${idPrefix}-${legKey}-flight`}>{t('ticketTravel.flightNumber')}</label>
            <input
              id={`${idPrefix}-${legKey}-flight`}
              type="text"
              value={leg.flightNumber}
              onChange={(e) => update('flightNumber', e.target.value)}
              required
            />
          </div>
          <div className="attendee-field">
            <label htmlFor={`${idPrefix}-${legKey}-airport`}>{t('ticketTravel.airport')}</label>
            <input
              id={`${idPrefix}-${legKey}-airport`}
              type="text"
              value={leg.airport}
              onChange={(e) => update('airport', e.target.value)}
              required
            />
          </div>
        </div>
      )}

      {mode === 'bus' && (
        <div className="attendee-fields travel-mode-fields">
          <div className="attendee-field">
            <label htmlFor={`${idPrefix}-${legKey}-bus`}>{t('ticketTravel.busDeparture')}</label>
            <input
              id={`${idPrefix}-${legKey}-bus`}
              type="text"
              value={leg.busDeparturePlace}
              onChange={(e) => update('busDeparturePlace', e.target.value)}
              required
            />
          </div>
        </div>
      )}
    </div>
  );
}

function TravelFormBody({
  rows,
  updateRowLeg,
  waiverAccepted,
  setWaiverAccepted,
  embedded,
  submitting,
  handleSubmit,
  t,
}) {
  const fields = (
    <>
      {rows.map((row, index) => (
        <div key={row.attendeeIndex} className="travel-attendee-block">
          <h3>
            {t('ticketTravel.personTitle', {
              name:
                `${row.firstName} ${row.lastName}`.trim()
                || t('ticketTravel.personFallback', { number: index + 1 }),
            })}
          </h3>
          <div className="attendee-fields travel-name-row">
            <div className="attendee-field">
              <label>{t('ticketPayment.lastName')}</label>
              <input type="text" value={row.lastName} readOnly disabled />
            </div>
            <div className="attendee-field">
              <label>{t('ticketPayment.firstName')}</label>
              <input type="text" value={row.firstName} readOnly disabled />
            </div>
          </div>

          <h4>{t('ticketTravel.outboundTitle')}</h4>
          <TravelLegFields
            legKey="outbound"
            leg={row.outbound}
            onChange={(leg) => updateRowLeg(index, 'outbound', leg)}
            t={t}
            idPrefix={`att-${index}`}
          />

          <h4>{t('ticketTravel.returnTitle')}</h4>
          <TravelLegFields
            legKey="return"
            leg={row.returnLeg}
            onChange={(leg) => updateRowLeg(index, 'returnLeg', leg)}
            t={t}
            idPrefix={`att-${index}`}
          />
        </div>
      ))}

      <div className="travel-waiver-block">
        <h3>{t('ticketTravel.waiverTitle')}</h3>
        <p className="travel-waiver-text">{t('ticketTravel.waiverText')}</p>
        <div className="radio-group">
          <label className="radio-label">
            <input
              type="radio"
              name="travel-waiver"
              value="true"
              checked={waiverAccepted === true}
              onChange={() => setWaiverAccepted(true)}
              required
            />
            <span>{t('ticketTravel.accept')}</span>
          </label>
          <label className="radio-label">
            <input
              type="radio"
              name="travel-waiver"
              value="false"
              checked={waiverAccepted === false}
              onChange={() => setWaiverAccepted(false)}
            />
            <span>{t('ticketTravel.refuse')}</span>
          </label>
        </div>
      </div>

      {!embedded && (
        <div className="travel-form-actions">
          <button type="submit" className="btn-continue" disabled={submitting}>
            {submitting ? t('ticketTravel.submitting') : t('ticketTravel.submit')}
          </button>
        </div>
      )}
    </>
  );

  if (embedded) {
    return <div className="ticket-travel-form">{fields}</div>;
  }

  return (
    <form className="ticket-travel-form" onSubmit={handleSubmit}>
      {fields}
    </form>
  );
}

const TicketOrderTravelForm = forwardRef(function TicketOrderTravelForm(
  { order, onSaved, showOrderSuccessNote = false, embedded = false },
  ref,
) {
  const { t } = useLanguage();
  const [rows, setRows] = useState(() => buildTravelFormStateFromOrder(order));
  const [waiverAccepted, setWaiverAccepted] = useState(
    order?.travelInformation?.waiverAccepted === true ? true : null,
  );
  const [submitting, setSubmitting] = useState(false);
  /** Personnes dont le voyage a été modifié à la main (ne plus recopier depuis la 1re). */
  const customizedRowIndicesRef = useRef(new Set());

  const attendeeSyncKey = useMemo(
    () => (order?.attendees || [])
      .map((a, i) => `${i}:${a.firstName || ''}:${a.lastName || ''}:${a.role || ''}`)
      .join('|'),
    [order?.attendees],
  );

  useEffect(() => {
    if (!embedded) return;
    setRows(buildTravelFormStateFromOrder(order));
    customizedRowIndicesRef.current = new Set();
  }, [embedded, attendeeSyncKey, order]);

  const buildPayload = () => ({
    waiverAccepted: true,
    perAttendee: rows.map((row) => ({
      attendeeIndex: row.attendeeIndex,
      firstName: row.firstName,
      lastName: row.lastName,
      outbound: row.outbound,
      returnLeg: row.returnLeg,
    })),
  });

  useImperativeHandle(
    ref,
    () => ({
      validate: () => validateTravelFormClient(rows, waiverAccepted, t),
      getPayload: buildPayload,
    }),
    [rows, waiverAccepted, t],
  );

  const updateRowLeg = (rowIndex, legKey, legValue) => {
    if (rowIndex > 0) {
      customizedRowIndicesRef.current.add(rowIndex);
    }

    setRows((prev) => {
      const next = [...prev];
      next[rowIndex] = { ...next[rowIndex], [legKey]: legValue };
      if (rowIndex === 0 && next.length > 1) {
        return syncRowsFromPrimary(next, customizedRowIndicesRef.current);
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateTravelFormClient(rows, waiverAccepted, t);
    if (validationError) {
      alert(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.put(`/ticket-orders/${order._id}/travel-information`, buildPayload());
      if (onSaved) {
        onSaved(res.data?.travelInformation);
      }
    } catch (error) {
      const data = error.response?.data;
      const msg =
        (data && typeof data === 'object' && data.error)
        || (typeof data === 'string' && data.includes('Cannot PUT')
          ? t('ticketTravel.submitError')
          : null)
        || error.message
        || t('ticketTravel.submitError');
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ticket-travel-form-section">
      {showOrderSuccessNote && (
        <p className="ticket-travel-order-success">{t('ticketTravel.afterOrderIntro')}</p>
      )}

      <div className="ticket-travel-warning" role="note">
        <p lang="fr">{t('ticketTravel.warningFr')}</p>
        <p lang="it">{t('ticketTravel.warningIt')}</p>
      </div>

      <h2>{t('ticketTravel.title')}</h2>
      <p className="ticket-travel-intro">
        {embedded
          ? t('ticketTravel.beforeOrderIntro')
          : rows.length > 1
            ? t('ticketTravel.introMulti')
            : t('ticketTravel.intro')}
      </p>
      {embedded && rows.length > 1 && (
        <p className="ticket-travel-intro">{t('ticketTravel.introMulti')}</p>
      )}

      <TravelFormBody
        rows={rows}
        updateRowLeg={updateRowLeg}
        waiverAccepted={waiverAccepted}
        setWaiverAccepted={setWaiverAccepted}
        embedded={embedded}
        submitting={submitting}
        handleSubmit={handleSubmit}
        t={t}
      />
    </div>
  );
});

TicketOrderTravelForm.displayName = 'TicketOrderTravelForm';

export default TicketOrderTravelForm;
